// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { BarChart3, TrendingUp, AlertTriangle, CheckCircle, Calendar, Download } from 'lucide-react';

const DATE_RANGES = [
  { label: 'Today',    days: 1  },
  { label: '7 Days',   days: 7  },
  { label: '30 Days',  days: 30 },
  { label: '90 Days',  days: 90 },
];

export default function ComplianceDashboardPage() {
  const [range, setRange]           = useState(7);
  const [sessions, setSessions]     = useState([]);
  const [incidents, setIncidents]   = useState([]);
  const [zoneStats, setZoneStats]   = useState([]);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    fetchData();
  }, [range]);

  const fetchData = async () => {
    setLoading(true);
    const since = new Date(Date.now() - range * 86400000).toISOString();

    const [{ data: sess }, { data: inc }, { data: zones }] = await Promise.all([
      supabase.from('grocery_cleaning_sessions')
        .select('*, grocery_zones(zone_name, zone_icon, zone_color)')
        .gte('created_at', since)
        .order('created_at', { ascending: false }),
      supabase.from('grocery_incidents')
        .select('*')
        .gte('created_at', since),
      supabase.from('grocery_zones').select('*').eq('active', true),
    ]);

    setSessions(sess || []);
    setIncidents(inc || []);

    // Zone compliance calculation
    const now   = Date.now();
    const stats = (zones || []).map(z => {
      const zoneSessions = (sess || []).filter(s => s.zone_id === z.id);
      const approved     = zoneSessions.filter(s => s.session_status === 'APPROVED');
      const total        = zoneSessions.length;
      const avgScore     = approved.length > 0
        ? Math.round(approved.reduce((a, s) => a + (s.score_pct || 100), 0) / approved.length)
        : null;
      const lastApproved = approved[0];
      const hoursSinceLast = lastApproved
        ? (now - new Date(lastApproved.approved_at)) / 3600000
        : Infinity;
      const isOverdue = hoursSinceLast > z.cleaning_frequency_hours;

      return { ...z, total, approved: approved.length, avgScore, hoursSinceLast, isOverdue };
    });

    stats.sort((a, b) => (b.isOverdue ? 1 : 0) - (a.isOverdue ? 1 : 0) || (a.hoursSinceLast - b.hoursSinceLast));
    setZoneStats(stats);
    setLoading(false);
  };

  const totalSessions    = sessions.length;
  const approvedSessions = sessions.filter(s => s.session_status === 'APPROVED').length;
  const rejectedSessions = sessions.filter(s => s.session_status === 'REJECTED').length;
  const pendingSessions  = sessions.filter(s => s.session_status === 'AWAITING_APPROVAL').length;
  const complianceRate   = totalSessions > 0 ? Math.round((approvedSessions / totalSessions) * 100) : 0;
  const avgScore         = sessions.filter(s => s.score_pct != null).length > 0
    ? Math.round(sessions.filter(s => s.score_pct != null).reduce((a, s) => a + s.score_pct, 0) / sessions.filter(s => s.score_pct != null).length)
    : 0;
  const openIncidents    = incidents.filter(i => i.status === 'OPEN').length;
  const criticalIncidents = incidents.filter(i => i.severity === 'critical' && i.status !== 'CLOSED').length;

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Compliance Dashboard</h1>
          <p className="page-subtitle">Store cleanliness performance and audit metrics</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {DATE_RANGES.map(r => (
            <button
              key={r.days}
              onClick={() => setRange(r.days)}
              style={{
                padding: '7px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                border: 'none',
                background: range === r.days ? '#0F766E' : '#CCFBF1',
                color: range === r.days ? '#fff' : '#0F766E',
              }}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="page-body">
        {/* KPI strip */}
        <div className="stats-grid">
          {[
            { label: 'Compliance Rate', value: `${complianceRate}%`, icon: '📊', color: complianceRate >= 80 ? '#10B981' : '#F59E0B' },
            { label: 'Avg Score',       value: `${avgScore}%`,       icon: '⭐', color: avgScore >= 80 ? '#10B981' : '#F59E0B' },
            { label: 'Sessions',        value: totalSessions,        icon: '📋', color: '#3B82F6' },
            { label: 'Approved',        value: approvedSessions,     icon: '✅', color: '#10B981' },
            { label: 'Rejected',        value: rejectedSessions,     icon: '❌', color: '#EF4444' },
            { label: 'Pending Approval',value: pendingSessions,      icon: '⏳', color: '#8B5CF6' },
            { label: 'Open Incidents',  value: openIncidents,        icon: '⚠️', color: '#F59E0B' },
            { label: 'Critical',        value: criticalIncidents,    icon: '🚨', color: '#DC2626' },
          ].map(s => (
            <div key={s.label} className="stat-card">
              <div style={{ fontSize: 22, marginBottom: 4 }}>{s.icon}</div>
              <div className="stat-value" style={{ color: s.color, fontSize: 20 }}>{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Overall compliance bar */}
        <div className="card" style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#134E4A' }}>Overall Compliance Rate</span>
            <span style={{
              fontSize: 20, fontWeight: 800,
              color: complianceRate >= 80 ? '#10B981' : complianceRate >= 60 ? '#F59E0B' : '#EF4444',
            }}>
              {complianceRate}%
            </span>
          </div>
          <div style={{ height: 16, background: '#CCFBF1', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: `${complianceRate}%`,
              background: complianceRate >= 80 ? 'linear-gradient(90deg, #10B981, #059669)'
                : complianceRate >= 60 ? 'linear-gradient(90deg, #F59E0B, #D97706)'
                : 'linear-gradient(90deg, #EF4444, #DC2626)',
              borderRadius: 8,
              transition: 'width 0.8s ease',
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11, color: '#94A3B8' }}>
            <span>0%</span>
            <span style={{ color: '#EF4444' }}>60% min acceptable</span>
            <span style={{ color: '#10B981' }}>80% target</span>
            <span>100%</span>
          </div>
        </div>

        {/* Zone-by-zone breakdown */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <BarChart3 size={18} color="#0F766E" />
            <span style={{ fontSize: 15, fontWeight: 700, color: '#134E4A' }}>Zone Compliance Breakdown</span>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Zone</th>
                  <th>Sessions</th>
                  <th>Approved</th>
                  <th>Avg Score</th>
                  <th>Status</th>
                  <th>Hours Since Clean</th>
                </tr>
              </thead>
              <tbody>
                {zoneStats.map(z => (
                  <tr key={z.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 18 }}>{z.zone_icon}</span>
                        <span style={{ fontWeight: 600 }}>{z.zone_name}</span>
                      </div>
                    </td>
                    <td>{z.total}</td>
                    <td>
                      <span style={{ fontWeight: 600, color: z.approved > 0 ? '#10B981' : '#94A3B8' }}>
                        {z.approved}
                      </span>
                    </td>
                    <td>
                      {z.avgScore != null ? (
                        <span style={{
                          fontWeight: 700,
                          color: z.avgScore >= 80 ? '#10B981' : z.avgScore >= 60 ? '#F59E0B' : '#EF4444',
                        }}>
                          {z.avgScore}%
                        </span>
                      ) : <span style={{ color: '#94A3B8' }}>—</span>}
                    </td>
                    <td>
                      {z.isOverdue ? (
                        <span className="badge badge-danger">Overdue</span>
                      ) : (
                        <span className="badge badge-success">Clean</span>
                      )}
                      {z.requires_supervisor_sign && (
                        <span className="badge badge-info" style={{ marginLeft: 4 }}>Sup. req'd</span>
                      )}
                    </td>
                    <td>
                      {z.hoursSinceLast === Infinity ? (
                        <span style={{ color: '#DC2626', fontWeight: 700 }}>Never cleaned</span>
                      ) : (
                        <span style={{ color: z.isOverdue ? '#DC2626' : '#10B981', fontWeight: 600 }}>
                          {z.hoursSinceLast.toFixed(1)}h
                          <span style={{ fontSize: 11, color: '#94A3B8', fontWeight: 400 }}> / {z.cleaning_frequency_hours}h target</span>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent incidents */}
        {incidents.length > 0 && (
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <AlertTriangle size={18} color="#F59E0B" />
              <span style={{ fontSize: 15, fontWeight: 700, color: '#134E4A' }}>Incidents ({range} days)</span>
            </div>

            {incidents.slice(0, 8).map(inc => (
              <div key={inc.id} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid #F0FDF9',
              }}>
                <div style={{
                  width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                  background: inc.severity === 'critical' ? '#DC2626'
                    : inc.severity === 'high' ? '#EF4444'
                    : inc.severity === 'medium' ? '#F59E0B' : '#94A3B8',
                }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#134E4A' }}>{inc.title}</div>
                  <div style={{ fontSize: 11, color: '#94A3B8' }}>
                    {inc.incident_type} · {new Date(inc.created_at).toLocaleDateString()}
                  </div>
                </div>
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
                  background: inc.status === 'RESOLVED' || inc.status === 'CLOSED' ? '#D1FAE5' : '#FEE2E2',
                  color: inc.status === 'RESOLVED' || inc.status === 'CLOSED' ? '#059669' : '#DC2626',
                }}>
                  {inc.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
